import { observable, action, runInAction, decorate } from "mobx";
import ZoneProvidersPriorityApi from "./api";

class ZoneProvidersPriorityStore {
    rootStore;
    apiClient;

    zoneId = undefined;
    list = [];
    total = 0;
    pageNumber = 1;
    pageSize = 25;

    providers = [];
    classes = [];

    isFetchingListInProgress = false;
    fetchingListError = undefined;

    isFetchingDictionariesInProgress = false;

    isSavingInProgress = false;
    savingError = undefined;

    isDeletingInProgress = false;
    deletingError = undefined;

    constructor(rootStore) {
        this.rootStore = rootStore;
        this.apiClient = new ZoneProvidersPriorityApi(rootStore.apiClient);
    }

    async fetchList({ zoneId, pageNumber, pageSize } = {}) {
        runInAction(() => {
            if (zoneId !== undefined) this.zoneId = zoneId;
            if (pageNumber !== undefined) this.pageNumber = pageNumber;
            if (pageSize !== undefined) this.pageSize = pageSize;
            this.isFetchingListInProgress = true;
            this.fetchingListError = undefined;
        });
        try {
            const data = await this.apiClient.list(this.zoneId, {
                pageNumber: this.pageNumber,
                pageSize: this.pageSize,
            });
            runInAction(() => {
                this.list = data?.items || [];
                this.total = data?.total_count ?? 0;
                this.isFetchingListInProgress = false;
            });
        } catch (err) {
            runInAction(() => {
                this.list = [];
                this.total = 0;
                this.fetchingListError = err;
                this.isFetchingListInProgress = false;
            });
        }
    }

    async fetchDictionaries() {
        runInAction(() => {
            this.isFetchingDictionariesInProgress = true;
        });
        try {
            const [providers, classes] = await Promise.all([
                this.apiClient.providers(),
                this.apiClient.classes(),
            ]);
            runInAction(() => {
                this.providers = providers?.items || [];
                this.classes = classes?.items || [];
                this.isFetchingDictionariesInProgress = false;
            });
        } catch (err) {
            runInAction(() => {
                this.isFetchingDictionariesInProgress = false;
            });
        }
    }

    async saveRecord(id, record) {
        runInAction(() => {
            this.isSavingInProgress = true;
            this.savingError = undefined;
        });
        try {
            const data = id
                ? await this.apiClient.update(this.zoneId, id, record)
                : await this.apiClient.create(this.zoneId, record);
            await this.fetchList();
            runInAction(() => {
                this.isSavingInProgress = false;
            });
            return data;
        } catch (err) {
            runInAction(() => {
                this.savingError = err;
                this.isSavingInProgress = false;
            });
            throw err;
        }
    }

    async deleteRecord(id) {
        runInAction(() => {
            this.isDeletingInProgress = true;
            this.deletingError = undefined;
        });
        try {
            const data = await this.apiClient.remove(this.zoneId, id);
            await this.fetchList();
            runInAction(() => {
                this.isDeletingInProgress = false;
            });
            return data;
        } catch (err) {
            runInAction(() => {
                this.deletingError = err;
                this.isDeletingInProgress = false;
            });
            throw err;
        }
    }
}

decorate(ZoneProvidersPriorityStore, {
    zoneId: observable,
    list: observable,
    total: observable,
    pageNumber: observable,
    pageSize: observable,
    providers: observable,
    classes: observable,
    isFetchingListInProgress: observable,
    fetchingListError: observable,
    isFetchingDictionariesInProgress: observable,
    isSavingInProgress: observable,
    savingError: observable,
    isDeletingInProgress: observable,
    deletingError: observable,
    fetchList: action,
    fetchDictionaries: action,
    saveRecord: action,
    deleteRecord: action,
});

export default ZoneProvidersPriorityStore;
